import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DescriptionIcon from "@mui/icons-material/Description";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MarkdownRenderer from "../../MarkdownRenderer";
import styles from "../agent-response.module.scss";
import axiosClient from "../../../api/AxiosClient";
import { useAppDispatch, RootState } from "../../../store";
import { useSelector } from "react-redux";
import { setSowAutoFillerFeedback } from "../../../store/filtersSlice";

type SowAutoFillerAgentProps = {
  parsedContent: any;
  messageId?: string;
};

// Component for accordion item - shows field with expand/collapse functionality
const AccordionRow = ({
  fieldName,
  value,
  onEdit,
  isModified = false,
}: {
  fieldName: string;
  value: string;
  onEdit: (fieldName: string, currentValue: string) => void;
  isModified?: boolean;
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [copySuccess, setCopySuccess] = React.useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent accordion toggle
    navigator.clipboard.writeText(value);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent accordion toggle
    onEdit(fieldName, value);
  };

  return (
    <div
      style={{
        border: "1px solid #b2e5de",
        borderRadius: "8px",
        marginBottom: "16px",
        overflow: "hidden",
        backgroundColor: isModified ? "#fff4e6" : "#fff",
      }}
    >
      <div
        style={{
          background: "#f0fbf9",
          padding: "16px 20px",
          cursor: "pointer",
          transition: "background 0.2s",
        }}
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f4f6")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "#f0fbf9")}
      >
        <h3
          style={{
            fontSize: "16px",
            fontWeight: 500,
            color: "#004141",
            margin: 0,
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {isOpen ? (
            <ExpandLessIcon htmlColor="#006d77" sx={{ fontSize: "24px" }} />
          ) : (
            <ExpandMoreIcon htmlColor="#006d77" sx={{ fontSize: "24px" }} />
          )}
          {fieldName}
          {isModified && (
            <Chip
              label="Modified"
              size="small"
              sx={{
                height: "18px",
                fontSize: "10px",
                backgroundColor: "#ff9800",
                color: "#fff",
                fontWeight: 600,
                marginLeft: "8px",
              }}
            />
          )}
        </h3>
      </div>
      {isOpen && (
        <div
          style={{
            padding: "20px",
            background: "#ffffff",
            position: "relative",
          }}
        >
          <Box sx={{ display: "flex", gap: 0.5, position: "absolute", top: "12px", right: "12px" }}>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={handleEdit}
                sx={{
                  padding: "3px",
                  minWidth: "28px",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#f3f4f6",
                  },
                }}
              >
                <EditIcon
                  style={{
                    color: "#9ca3af",
                    fontSize: "20px",
                  }}
                />
              </IconButton>
            </Tooltip>
            <Tooltip title={copySuccess ? "Copied!" : "Copy"}>
              <IconButton
                size="small"
                onClick={handleCopy}
                sx={{
                  padding: "3px",
                  minWidth: "28px",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#f3f4f6",
                  },
                }}
              >
                <ContentCopyIcon
                  style={{
                    color: copySuccess ? "#00a888" : "#9ca3af",
                    fontSize: "20px",
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography
            component="div"
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              color: "#403f3f",
              fontSize: "14px",
              lineHeight: 1.6,
              paddingRight: "70px",
            }}
          >
            {value}
          </Typography>
        </div>
      )}
    </div>
  );
};

export default function SowAutoFillerAgent({
  parsedContent,
  messageId = "",
}: SowAutoFillerAgentProps): JSX.Element {
  const dispatch = useAppDispatch();
  const { sowAutoFillerFeedback } = useSelector((state: RootState) => state.filters);
  const currentFeedback = sowAutoFillerFeedback[messageId] || { sow_modify_array: [] };
  const [isDownloading, setIsDownloading] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentEdit, setCurrentEdit] = useState({
    fieldName: "",
    oldValue: "",
    newValue: "",
  });

  // Extract data from standard structure (same as other agents)
  const data = parsedContent?.finalAnswer || parsedContent;

  const message = data?.message || "";
  const question = data?.question || "";
  const detectedCategory = data?.detected_category || "";
  const extractedData = data?.extracted_data || {};
  const filledDocumentPath = data?.filled_document_path || "";
  const downloadUrl = data?.download_url || "";
  const filledFields = data?.filled_fields || [];
  const missingFields = data?.missing_fields || [];
  const status = data?.status || "";

  // Extract filename from path or download URL
  const getFilenameFromPath = (path: string): string => {
    if (!path) return "SOW_Document.docx";
    const parts = path.split(/[\\\/]/);
    return parts[parts.length - 1] || "SOW_Document.docx";
  };

  const filename = getFilenameFromPath(filledDocumentPath || downloadUrl);

  // Extract header from message (before the table)
  const getMessageHeader = (): string => {
    if (!message) return "";

    // Find where the table starts
    const tableStart = message.indexOf("| Field |");
    if (tableStart === -1) return message;

    let header = message.substring(0, tableStart).trim();

    // Remove "Changes Applied" and "What Changed" sections
    // Remove everything from "**Changes Applied!**" to "---" (or "## SOW")
    header = header.replace(/\*\*Changes Applied!\*\*[\s\S]*?---/g, "");
    header = header.replace(/###?\s*What Changed:[\s\S]*?(?=##|$)/g, "");

    // Clean up extra newlines and dashes
    header = header.replace(/---+/g, "");
    header = header.replace(/\n{3,}/g, "\n\n");

    return header.trim();
  };

  // Map display field names to backend field keys
  const fieldKeyMap: { [key: string]: string } = {
    "Vendor Name": "vendor_name",
    "Services": "services_description",
    "Fees": "fees",
    "Duration": "contract_duration",
    "Term": "contract_duration",
    "Effective Date": "effective_date",
    "SOW Effective Date": "effective_date",
    "Performance Standards": "performance_standards",
    "Deliverables": "deliverables",
    "Milestones": "milestones",
    "Milestones, Milestone Dates or other timescales for delivery": "milestones",
    "Timescales": "timescales",
    "Supplier Project Representative": "supplier_project_representative",
    "G42 Project Representative": "g42_project_representative",
    "Background and Objectives": "background_objectives",
    "Assumptions": "assumptions",
    "Acceptance Criteria": "acceptance_criteria",
    "Number of Resources": "num_resources",
    "Skill Level": "skill_level",
    "Work Location": "work_location",
    "Work Schedule": "work_schedule",
    "Daily Rate": "daily_rate",
    "Purchase Order Number": "purchase_order_number",
    "Invoices": "invoice_email",
  };

  // Map field names to their full values from extractedData or modifications
  const getFieldValue = (fieldKey: string): string => {
    const backendFieldKey = fieldKeyMap[fieldKey];

    // Check if this field has been modified in Redux
    const modification = currentFeedback.sow_modify_array.find(
      (mod: any) => mod.field === backendFieldKey
    );

    // If modified, return the new value; otherwise return original from extractedData
    if (modification) {
      return modification.new_value;
    }

    const fieldMap: { [key: string]: string } = {
      "Vendor Name": extractedData.vendor_name || "",
      "Services": extractedData.services_description || "",
      "Fees": extractedData.fees || "",
      "Duration": extractedData.contract_duration || extractedData.timescales || "",
      "Term": extractedData.contract_duration || extractedData.timescales || "",
      "Effective Date": extractedData.effective_date || "",
      "SOW Effective Date": extractedData.effective_date || "",
      "Performance Standards": extractedData.performance_standards || "",
      "Deliverables": extractedData.deliverables || "",
      "Milestones": extractedData.milestones || "",
      "Milestones, Milestone Dates or other timescales for delivery": extractedData.milestones || "",
      "Timescales": extractedData.timescales || "",
      "Supplier Project Representative": extractedData.supplier_project_representative || "",
      "G42 Project Representative": extractedData.g42_project_representative || "",
      "Background and Objectives": extractedData.background_objectives || "",
      "Assumptions": extractedData.assumptions || "",
      "Acceptance Criteria": extractedData.acceptance_criteria || "",
      "Number of Resources": extractedData.num_resources || "",
      "Skill Level": extractedData.skill_level || "",
      "Work Location": extractedData.work_location || "",
      "Work Schedule": extractedData.work_schedule || "",
      "Daily Rate": extractedData.daily_rate || "",
      "Purchase Order Number": extractedData.purchase_order_number || "",
      "Invoices": extractedData.invoice_email || "",
    };
    return fieldMap[fieldKey] || "";
  };

  // Check if a field has been modified
  const isFieldModified = (fieldKey: string): boolean => {
    const backendFieldKey = fieldKeyMap[fieldKey];
    return currentFeedback.sow_modify_array.some(
      (mod: any) => mod.field === backendFieldKey
    );
  };

  // Define all possible fields in display order
  const getAllFieldsFromData = (): string[] => {
    if (!extractedData || Object.keys(extractedData).length === 0) return [];

    // Define field order and their display names
    const fieldOrder: Array<{ key: string; display: string }> = [
      { key: "vendor_name", display: "Vendor Name" },
      { key: "services_description", display: "Services" },
      { key: "effective_date", display: "SOW Effective Date" },
      { key: "background_objectives", display: "Background and Objectives" },
      { key: "deliverables", display: "Deliverables" },
      { key: "fees", display: "Fees" },
      { key: "performance_standards", display: "Performance Standards" },
      { key: "milestones", display: "Milestones, Milestone Dates or other timescales for delivery" },
      { key: "supplier_project_representative", display: "Supplier Project Representative" },
      { key: "g42_project_representative", display: "G42 Project Representative" },
      { key: "assumptions", display: "Assumptions" },
      { key: "contract_duration", display: "Term" },
      { key: "work_location", display: "Work Location" },
      { key: "work_schedule", display: "Work Schedule" },
      { key: "num_resources", display: "Number of Resources" },
      { key: "skill_level", display: "Skill Level" },
      { key: "daily_rate", display: "Daily Rate" },
      { key: "acceptance_criteria", display: "Acceptance Criteria" },
      { key: "purchase_order_number", display: "Purchase Order Number" },
      { key: "invoice_email", display: "Invoices" },
    ];

    // Return only fields that have values
    return fieldOrder
      .filter((field) => {
        const value = extractedData[field.key];
        return value !== null && value !== undefined && value !== "";
      })
      .map((field) => field.display);
  };

  const messageHeader = getMessageHeader();
  const tableFields = getAllFieldsFromData();

  // Format field names from snake_case to Title Case
  const formatFieldName = (fieldName: string): string => {
    return fieldName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Handle edit button click
  const handleEdit = (fieldName: string, currentValue: string) => {
    setCurrentEdit({
      fieldName,
      oldValue: currentValue,
      newValue: currentValue,
    });
    setIsEditModalOpen(true);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setCurrentEdit({
      fieldName: "",
      oldValue: "",
      newValue: "",
    });
  };

  // Handle save edited value
  const handleSaveEdit = () => {
    if (currentEdit.newValue.trim() === "") {
      alert("Value cannot be empty");
      return;
    }

    if (currentEdit.newValue === currentEdit.oldValue) {
      alert("No changes detected");
      handleCloseModal();
      return;
    }

    const fieldKey = fieldKeyMap[currentEdit.fieldName] || currentEdit.fieldName;

    // Create modification object
    const modificationObject = {
      field: fieldKey,
      old_value: currentEdit.oldValue,
      new_value: currentEdit.newValue,
    };

    // Append to existing modifications array (not replace it)
    dispatch(
      setSowAutoFillerFeedback({
        messageId,
        feedback: {
          sow_modify_array: [...currentFeedback.sow_modify_array, modificationObject],
        },
      })
    );

    handleCloseModal();
  };

  // Handle document download from backend
  const handleDownload = async () => {
    if (!downloadUrl) {
      console.error("No download URL provided");
      alert("Download URL is not available. Please try again.");
      return;
    }

    setIsDownloading(true);

    try {
      console.log("Downloading file from:", downloadUrl);

      // Make API request to download the file
      const response = await axiosClient.get(downloadUrl, {
        responseType: "blob",
      });

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log("Download completed successfully");
    } catch (error) {
      console.error("Error downloading file:", error);
      alert("Failed to download the document. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className={styles.pressReleaseContent}>
      {/* For showing_checklist status, display the full message */}
      {status === "showing_checklist" && message && (
        <Box sx={{ marginBottom: 3 }}>
          <MarkdownRenderer content={message} />
        </Box>
      )}

      {/* Main Message Header (before table) - only show if not showing_checklist */}
      {status !== "showing_checklist" && messageHeader && (
        <Box sx={{ marginBottom: 3 }}>
          <MarkdownRenderer content={messageHeader} />
        </Box>
      )}

      {/* Extracted Information Accordion - only show if not showing_checklist */}
      {status !== "showing_checklist" && tableFields.length > 0 && (
        <Box sx={{ marginBottom: 3, display: "flex", flexDirection: "column", gap: 1 }}>
          {tableFields.map((fieldName, index) => {
            const value = getFieldValue(fieldName);
            return value ? (
              <AccordionRow
                key={index}
                fieldName={fieldName}
                value={value}
                onEdit={handleEdit}
                isModified={isFieldModified(fieldName)}
              />
            ) : null;
          })}
        </Box>
      )}

      {/* Document Download Section - only show if not showing_checklist */}
      {status !== "showing_checklist" && (filledDocumentPath || downloadUrl) && (
        <Paper
          elevation={0}
          sx={{
            padding: 2.5,
            marginBottom: 3,
            backgroundColor: "#f0fbf9",
            border: "1px solid #B2E5DE",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <DescriptionIcon sx={{ fontSize: 32, color: "#00a888" }} />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#004141" }}>
                  Ready to download
                </Typography>
                <Typography variant="body2" sx={{ color: "#403f3f" }}>
                  {filename}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={isDownloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
              onClick={handleDownload}
              disabled={isDownloading || !downloadUrl}
              sx={{
                textTransform: "none",
                paddingX: 3,
                backgroundColor: "#5fcfac",
                "&:hover": { backgroundColor: "#4fb89a" },
              }}
            >
              {isDownloading ? "Downloading..." : "Download DOCX"}
            </Button>
          </Box>
        </Paper>
      )}

      {/* SOW sections found and filled - only show if not showing_checklist */}
      {status !== "showing_checklist" && filledFields.length > 0 && (
        <Box sx={{ marginBottom: 3 }}>
          <Paper
            elevation={0}
            sx={{
              padding: 2,
              backgroundColor: "#f0fbf9",
              border: "1px solid #B2E5DE",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <CheckCircleIcon sx={{ color: "#00a888", fontSize: 20 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#004141" }}>
                SOW sections found and filled ({filledFields.length})
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {filledFields.map((field: string, index: number) => (
                <Chip
                  key={index}
                  label={field}
                  size="small"
                  sx={{
                    backgroundColor: "#fff",
                    fontSize: "11px",
                    border: "1px solid #B2E5DE",
                    color: "#403f3f",
                  }}
                />
              ))}
            </Box>
          </Paper>
        </Box>
      )}

      {/* Missing Fields - only show if not showing_checklist */}
      {status !== "showing_checklist" && missingFields.length > 0 && (
        <Box sx={{ marginBottom: 3 }}>
          <Paper
            elevation={0}
            sx={{
              padding: 2,
              backgroundColor: "#fef9f5",
              border: "1px solid #fed7aa",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <WarningAmberIcon sx={{ color: "#905d13", fontSize: 20 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#905d13" }}>
                SOW sections not found in input documents ({missingFields.length})
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {missingFields.map((field: string, index: number) => (
                <Chip
                  key={index}
                  label={formatFieldName(field)}
                  size="small"
                  sx={{
                    backgroundColor: "#fff",
                    fontSize: "11px",
                    border: "1px solid #fed7aa",
                    color: "#905d13",
                  }}
                />
              ))}
            </Box>
          </Paper>
        </Box>
      )}

      {/* Question Section */}
      {question && (
        <div
          style={{
            marginTop: "20px",
            paddingTop: "16px",
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <p
            style={{
              fontSize: "16px",
              color: "#004141",
              fontWeight: "600",
              margin: 0,
              whiteSpace: "pre-line",
            }}
          >
            {question}
          </p>
        </div>
      )}

      {/* Edit Modal Dialog */}
      <Dialog
        open={isEditModalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            backgroundColor: "#f0fbf9",
            color: "#004141",
            fontWeight: 600,
          }}
        >
          Edit {currentEdit.fieldName}
        </DialogTitle>
        <DialogContent sx={{ paddingTop: "20px !important" }}>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            label="Value"
            value={currentEdit.newValue}
            onChange={(e) =>
              setCurrentEdit({ ...currentEdit, newValue: e.target.value })
            }
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: "#5fcfac",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#5fcfac",
                },
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#004141",
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button
            onClick={handleCloseModal}
            sx={{
              textTransform: "none",
              color: "#403f3f",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            sx={{
              textTransform: "none",
              backgroundColor: "#5fcfac",
              "&:hover": {
                backgroundColor: "#4fb89a",
              },
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
